import moment from 'moment';
import ngFileSaver from 'angular-file-saver';
import ngTranslate from 'angular-translate';
import get from 'lodash/get';

import { AppSettingsModule } from 'services/settings.provider';
import { DataServiceModule } from 'services/data/data.service';
import { CartServiceModule } from 'services/data/carts.service';
import { LaunchDarklyServiceModule } from 'services/access/launchDarkly/launchDarkly.service';

export const AnalyticsService = angular
    .module('app.pages.analytics.analyticsService', [
        AppSettingsModule,
        DataServiceModule,
        CartServiceModule,
        ngFileSaver,
        ngTranslate,
        'ngMaterial',
        LaunchDarklyServiceModule
    ]).service('analyticsService', (
        dataService,
        settings,
        FileSaver,
        $translate,
        $mdDialog,
        cartService,
        launchDarklyService
    ) => {
        function getAnalytics(dateRange = generateDateRangeObject(30)) {
            const dateRangeQuery = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
            return dataService.getAll(`${settings.api.analytics}${dateRangeQuery}`);
        }

        function generateDateRangeObject(days, end = null) {
            let endDate = end !== null ?
                moment().subtract(end, 'days').format('YYYY-MM-DD').toString() :
                moment().format('YYYY-MM-DD').toString();

            return {
                startDate: moment().subtract(days, 'days').format('YYYY-MM-DD').toString(),
                endDate
            };
        }

        function generateTimestampRangeObject(days, end = null) {
            let endDate = end !== null ?
                moment().subtract(end, 'days').toISOString() :
                moment().toISOString();

            return {
                startDate: moment().subtract(days, 'days').toISOString(),
                endDate
            };
        }

        function getSalesCsv(dateRange = generateDateRangeObject(30)) {
            const dateRangeQuery = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
            return dataService.get(`${settings.api.analytics}/sales/export${dateRangeQuery}`, null, null, null, null)
                .then(response => saveStringToCsv(response.csv, 'VolusionSalesExport.csv'));
        }

        function exportCarts(dateRange = generateDateRangeObject(30)) {
            const options = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            };
            const headers = { Accept: 'text/csv' };
            return dataService.getAll(settings.api.abandonedCartsExport, options, headers)
                .then(response => saveStringToCsv(response, 'VolusionCartsExport.csv'));
        }

        function saveStringToCsv(string, fileName) {
            const blob = new Blob([string], { type: 'text/csv' });
            FileSaver.saveAs(blob, fileName);
            return blob;
        }

        /**
         *  @param {object} { title: 'string', description: 'string' }
        */
        function showError(errorObject) {
            // Need to pass an array to `this.$translate`
            return $translate(Object.values(errorObject)).then((translations) => {
                return $mdDialog.show(
                    $mdDialog.alert()
                        .title(translations[errorObject.title])
                        .textContent(translations[errorObject.description])
                        .ok('Ok')
                );
            });
        }

        function getAbandonedCartsData({
            count = 25,
            days = 30,
            page = 1
        } = { count: 25, days: 30, page: 1 }) {
            const dates = this.generateTimestampRangeObject(days);
            const returnHeaders = true;

            return cartService.getCarts({
                first: count,
                skip: (page - 1) * count,
                filter: `createdAt gt ${dates.startDate}`
            }, returnHeaders).then(({ data, headers }) => {
                const first = Number(headers['x-first']);
                const skip = Number(headers['x-skip']);
                const totalCount = Number(headers['x-total-count']);

                return {
                    items: data.carts.map(item => this.createViewModelFromCart(item)),
                    pageSize: first,
                    pageCount: Math.ceil(totalCount / first),
                    startIndex: skip,
                    totalCount: totalCount
                };
            });
        }

        function getLegacyAbandonedCartsData({ count = 25, days = 30, page = 1 }) {
            const { startDate, endDate } = this.generateDateRangeObject(days);
            return cartService.getLegacyAbandonedCarts({
                pageSize: count,
                startIndex: (page - 1) * count,
                startDate,
                endDate
            }).then(data => ({
                ...data,
                items: data.items.map(item => this.createViewModelFromLegacyCart(item))
            }));
        }

        function getAbandonedCart(cartId) {
            return launchDarklyService.getFeature('use-new-carts-in-checkout').then((newCartsEnabled) => {
                if (!newCartsEnabled) {
                    return cartService.getLegacyCart(cartId)
                        .then(cart => this.createViewModelFromLegacyCart(cart));
                }
                return cartService.getCart(cartId)
                    .then(cart => this.createViewModelFromCart(cart));
            });
        }

        function createViewModelFromLegacyCart(legacyCart) {
            return {
                id: legacyCart.id,
                createdAt: legacyCart.createdOn,
                updatedAt: legacyCart.updatedOn,
                total: legacyCart.total,
                taxAmount: legacyCart.taxAmount,
                grandTotal: legacyCart.grandTotal,
                shippingAmount: legacyCart.shippingMethod?.shippingCost,
                items: legacyCart.items.map(item => ({
                    imageSrc: get(item, 'product.images[0].imageLink.fullUri'),
                    quantity: item.quantity,
                    name: item.product.name,
                    salePrice: item.product.pricing.salePrice,
                    listPrice: item.product.pricing.listPrice
                })),
                shopper: {
                    email: legacyCart.contactInfo?.email,
                    firstName: get(
                        legacyCart,
                        'shippingAddress.firstName',
                        get(legacyCart, 'billingAddress.firstName')
                    ),
                    lastName: get(legacyCart, 'shippingAddress.lastName', get(legacyCart, 'billingAddress.lastName')),
                    phone: legacyCart.contactInfo?.phoneNumber,
                    billingAddress: {
                        firstName: legacyCart.billingAddress?.firstName,
                        lastName: legacyCart.billingAddress?.lastName,
                        address1: legacyCart.billingAddress?.address1,
                        address2: legacyCart.billingAddress?.address2,
                        city: legacyCart.billingAddress?.city,
                        state: legacyCart.billingAddress?.state,
                        zip: legacyCart.billingAddress?.postalCode
                    },
                    shippingAddress: {
                        firstName: legacyCart.shippingAddress?.firstName,
                        lastName: legacyCart.shippingAddress?.lastName,
                        address1: legacyCart.shippingAddress?.address1,
                        address2: legacyCart.shippingAddress?.address2,
                        city: legacyCart.shippingAddress?.city,
                        state: legacyCart.shippingAddress?.state,
                        zip: legacyCart.shippingAddress?.postalCode
                    }
                }
            };
        }

        function createViewModelFromCart(cart) {
            const getCartTotal = cartItems =>
                cartItems.reduce((acc, curr) => (acc + (curr.price.amount * curr.quantity)) / 100, 0);
            const getCartGrandTotal = _cart => getCartTotal(_cart.items);

            // TODO: fill in null values with data once it's available
            return {
                id: cart.cartId,
                createdAt: cart.createdAt,
                updatedAt: cart.updatedAt,
                total: getCartTotal(cart.items),
                taxAmount: null,
                grandTotal: getCartGrandTotal(cart),
                shippingAmount: null,
                items: cart.items?.map(item => ({
                    imageSrc: get(item, 'imageSrc'),
                    quantity: item.quantity,
                    name: item.productName,
                    salePrice: null,
                    listPrice: item.price.amount / 100
                })),
                shopper: {
                    email: cart.shopperEmail,
                    firstName: null,
                    lastName: null,
                    phone: null,
                    billingAddress: {
                        firstName: null,
                        lastName: null,
                        address1: null,
                        address2: null,
                        city: null,
                        state: null,
                        zip: null
                    },
                    shippingAddress: {
                        firstName: null,
                        lastName: null,
                        address1: null,
                        address2: null,
                        city: null,
                        state: null,
                        zip: null
                    }
                }
            };
        }

        return {
            getAbandonedCartsData,
            getLegacyAbandonedCartsData,
            getAnalytics,
            generateDateRangeObject,
            getSalesCsv,
            exportCarts,
            showError,
            generateTimestampRangeObject,
            createViewModelFromLegacyCart,
            createViewModelFromCart,
            getAbandonedCart
        };
    }).name;



// WEBPACK FOOTER //
// ./src/app/pages/analytics/analytics.service.js